'use strict';

// external imports
import React, {
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import _ from 'lodash';
import {v4 as uuidV4} from 'uuid';

// prime react imports
import {Button} from 'primereact/button';
import {ChevronDownIcon} from 'primereact/icons/chevrondown';
import {ChevronRightIcon} from 'primereact/icons/chevronright';
import {ContextMenu} from 'primereact/contextmenu';
import {
    MenuItem,
    MenuItemCommandEvent
} from 'primereact/menuitem';
import {Panel} from 'primereact/panel';
import {Tooltip} from 'primereact/tooltip';
import {
    Tree,
    TreeEventNodeEvent,
    TreeSelectionEvent
} from 'primereact/tree';
import TreeNode from 'primereact/treenode';
import {TreeTableExpandedKeysType} from 'primereact/treetable';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item, {
    ItemAttributesForCreation
} from '~/shared/src/ci/Item';
import Reference, {
    ReferenceAttributesForCreation
} from '~/shared/src/ci/Reference';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {
    aliasSeparator,
    buildDataDescriptor,
    DataDescriptor,
    DataObjectProperties,
    DataStructure,
    extractDataObjectsFromDataStructure,
    getDataObject,
    getParentDataObject,
    getReferenceSetsForDataObject
} from '~/shared/src/utilities/dataStructure';

// frontend components import
import ContentButton, {
    ReferenceType as ContentButtonType
} from '~/frontend/src/components/base/shared/ContentButton';
import getAddItemDialog from '~/frontend/src/components/base/shared/tree/ItemTree/AddTreeNodePanel';

// frontend imports
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {resolveLigatureIcon} from '~/frontend/src/utilities/renderHelpers';
import {getPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {
    clearElusiveData,
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import addItem from '~/frontend/src/actions/data/addItem';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import updateReference from '~/frontend/src/actions/data/updateReference';
import setPanelProps from '~/frontend/src/actions/panelManager/setPanelProps';

type ExtendTreeNode = TreeNode.TreeNode & {
    key : string;
    label : string;
    tooltip? : React.ReactNode;
    dataObject? : DataObjectProperties;
    children? : ExtendTreeNode[];
    data : {
        buttons? : React.ReactNode[];
        dataDescriptorAlias? : string[];
    };
}

export type DefaultProps = {

    readOnly? : boolean;

    // props to be passed to ItemTree
    expandedKeys? : TreeTableExpandedKeysType;

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    scrollTop? : number;
    scrollLeft? : number;
};
export const defaultProps : Required<DefaultProps> = {
    readOnly                     : false,
    expandedKeys                 : {},
    additionalElusiveDataContexts: [],
    scrollTop                    : 0,
    scrollLeft                   : 0
};

export type Props = {
    uid : string;
    parentUid : string;

    dataContext : string;

    rootItemId : number;

    onMarkLayoutElementsForAlias? : ( alias : string | undefined ) => void;

    selectedLayoutElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectLayoutElement? : ( id? : number ) => void;

    nodeIdToPanelKeyMap? : {
        [ nodeId : string ] : string
    };
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ReferenceType = {
    markFieldsForAlias : ( alias : string | undefined ) => void;
};

type ElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    dataStructure? : DataStructure;
    treeData? : ExtendTreeNode[];
    scrollPositions? : {
        [ treeNodeDataKey : string ] : number;
    }
};


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;


const DesignerTree = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const dispatch : AppDispatch                     = useDispatch(),
          {
              additionalElusiveDataContexts,
              dataContext,
              expandedKeys,
              nodeIdToPanelKeyMap,
              onMarkLayoutElementsForAlias,
              onSelectLayoutElement,
              parentUid,
              readOnly,
              rootItemId,
              scrollLeft,
              scrollTop,
              selectedLayoutElementId,
              uid
          }                                          = props,
          user                                       = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                    = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          metaModelTimestamp                         = useSelector( ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0 ),
          elusiveData                                = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          allElusiveData                             = useSelector( ( state : StoreState ) => state.elusiveData ),
          lastParams                                 = useRef<any>( null ),
          initialRendered                            = useRef<boolean>( false ),
          treeElement                                = useRef<HTMLDivElement | null>( null ),
          contextMenu                                = useRef<ContextMenu | null>( null ),
          nodeKeyToDataDescriptorAliasesRepresenting = useRef<{ [ nodeKey : string ] : string[] }>( {} ),
          nodeButtons                                = useRef<{
              [ buttonId : string ] : { justInteracted : boolean, content? : ContentButtonType }
          }>( {} ),
          ignoreSelectionChange                      = useRef<boolean>( false ),
          [ contextMenuNode, setContextMenuNode ]    = useState<string | undefined>( undefined ),
          [ aliasToMark, setAliasToMark ]            = useState<string | undefined>( undefined ),
          language                                   = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          globalDataVersions                         = useMemo(
              () => {
                  return JSON.stringify( allElusiveData?.[ dataContext ]?.data ?? {} );
              },
              [
                  allElusiveData?.[ dataContext ]?.data
              ]
          ),
          selectedDataDescriptorAlias                = useMemo(
              () => {
                  let retVal : string | undefined = undefined;

                  if ( selectedLayoutElementId ) {
                      const selectedLayoutElementItem = global.data[ dataContext ]?.items[ selectedLayoutElementId ];

                      if ( selectedLayoutElementItem ) {
                          for ( const itemReferenceId of selectedLayoutElementItem.references ) {
                              const reference                                    = global.data[ dataContext ]?.references?.[ itemReferenceId ],
                                    dataObjectProvidesDataForItemReferenceTypeId = metaModel[ selectedLayoutElementItem.schema ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', selectedLayoutElementItem.itemTypeId, 'providesDataFor', 'src' )?.[ 0 ]?.id;

                              if (
                                  reference
                                  && reference.destItemId === selectedLayoutElementItem.id
                                  && reference.referenceSetId === dataObjectProvidesDataForItemReferenceTypeId
                                  && reference.srcItem
                              ) {
                                  const selectedLayoutElementItemAttributes = dataExtraction.extractPropertiesOfItem( selectedLayoutElementItem ),
                                        dataObjectAttributes                = dataExtraction.extractPropertiesOfItem( reference.srcItem ),
                                        attributePath                       = selectedLayoutElementItemAttributes.attributePath as string | undefined,
                                        dataDescriptor                      = attributePath && dataObjectAttributes ? buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties ) : undefined;

                                  if ( dataDescriptor?.alias ) {
                                      retVal = dataDescriptor.alias;
                                  }

                                  break;
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  globalDataVersions,
                  selectedLayoutElementId
              ]
          ),
          rootItem                                   = useMemo(
              () => {
                  return global.data[ dataContext ]?.items[ rootItemId ];
              },
              [
                  dataContext,
                  rootItemId,
                  globalDataVersions
              ]
          ),
          dataStructure                              = useMemo(
              () => {
                  let retVal = rootItem ? elusiveData?.dataStructure : undefined;

                  if (
                      (
                          !retVal
                          || dataContext !== lastParams.current?.dataContext
                          || globalDataVersions !== lastParams.current?.globalDataVersions
                          || rootItem !== lastParams.current?.formItem
                      )
                      && rootItem
                  ) {
                      const newDataStructure = dataExtraction.extractDataStructure( rootItem, dataContext, true );

                      if ( JSON.stringify( newDataStructure ?? {} ) !== JSON.stringify( retVal ?? {} ) ) {
                          retVal = newDataStructure;

                          dispatch( setElusiveData( uid, { dataStructure: newDataStructure } ) );
                      }
                  }

                  return retVal;
              },
              [
                  dataContext,
                  rootItem,
                  globalDataVersions
              ]
          ),
          addNewNode                                 = ( dataObjectToUse : DataObjectProperties, dataStructureToUse : DataStructure, item : ItemAttributesForCreation | Item, reference : ReferenceAttributesForCreation ) => {
              const dataObjectSchema       = ( dataObjectToUse.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                    dataObjectItemType     = metaModel[ dataObjectSchema ].getItemType( item.itemTypeId )?.name,
                    dataObjectReferenceSet = metaModel[ dataObjectSchema ].getReferenceSet( reference.referenceSetId );

              if ( dataStructureToUse && dataObjectItemType && dataObjectReferenceSet ) {
                  const activeStatusId                      = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                        dataObjectItemTypeId                = metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                        objectTypePropertySetId             = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectType' )!.id,
                        objectSchemaPropertySetId           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectSchema' )!.id,
                        isReferencedToReferenceSetId        = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dataObjectItemTypeId, dataObjectItemTypeId, 'isReferencedTo', 'dest' )![ 0 ].id,
                        referenceConfigurationPropertySetId = metaModel[ 'CFG' ].getReferencePropertySetForReferenceSetAndPropertyType( isReferencedToReferenceSetId, 'ReferenceConfiguration' )!.id,
                        referenceDirection                  = dataObjectReferenceSet.srcItemType.name === dataObjectReferenceSet.destItemType.name
                            ? reference.srcItemId
                                ? 'SrcToDest'
                                : 'DestToSrc'
                            : dataObjectReferenceSet.srcItemType.name === dataObjectItemType
                                ? 'SrcToDest'
                                : 'DestToSrc';

                  const newItem = dispatch( addItem(
                      dataContext,
                      {
                          statusId  : activeStatusId,
                          itemTypeId: dataObjectItemTypeId,
                          key       : `${rootItem.key}:${dataObjectItemType}:${uuidV4().substring( 0, 8 )}`,
                          name      : dataObjectItemType,
                          properties: [
                              {
                                  propertySetId: objectTypePropertySetId,
                                  value        : dataObjectItemType,
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: objectSchemaPropertySetId,
                                  value        : dataObjectSchema.toLowerCase(),
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG',
                      additionalElusiveDataContexts
                  ) );

                  const parentReference = dispatch( addReference(
                      dataContext,
                      {
                          referenceSetId: isReferencedToReferenceSetId,
                          statusId      : activeStatusId,
                          srcItemId     : newItem.id,
                          destItemId    : dataObjectToUse.origId,
                          enumProperties: [
                              {
                                  referenceId           : Reference.uniqueIdCount,
                                  referencePropertySetId: referenceConfigurationPropertySetId,
                                  properties            : [
                                      {
                                          referenceId           : Reference.uniqueIdCount,
                                          referencePropertySetId: referenceConfigurationPropertySetId,
                                          key                   : dataObjectReferenceSet?.referenceType.name,
                                          value                 : `{ "referenceDirection": "${referenceDirection}" }`,
                                          statusId              : activeStatusId
                                      }
                                  ]
                              }
                          ]
                      },
                      'CFG',
                      additionalElusiveDataContexts
                  ) );

                  if ( parentReference ) {
                      newItem.registerReference( parentReference );
                  }

                  // select new element
                  onSelectLayoutElement?.( newItem.id );
              }
          },
          deleteOrUndeleteNode                       = ( dataObjectToUse : DataObjectProperties ) => {
              const dataObjectItem              = global.data[ dataContext ].items[ dataObjectToUse.origId ],
                    dataObjectItemDeleted       = dataObjectItem.isDeleted(),
                    referenceToParentDataObject = global.data[ dataContext ]?.references?.[ dataObjectItem.references.find(
                        ( referenceId ) => {
                            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                            let retVal : boolean = false;

                            if (
                                reference
                                && reference.srcItemId === dataObjectItem.id
                                && reference.referenceSet.referenceType.name === 'isReferencedTo'
                            ) {
                                retVal = true
                            }

                            return retVal;
                        }
                    ) ?? 0 ],
                    formPanelKey                = nodeIdToPanelKeyMap?.[ dataObjectToUse.id ]?.replace( '_attributesForm', '' );

              // undelete
              if ( dataObjectItemDeleted ) {
                  if ( referenceToParentDataObject ) {
                      dispatch( updateReference(
                          dataContext,
                          referenceToParentDataObject.id,
                          { statusId: referenceToParentDataObject.attributesModified.get( 'statusId' ) as number },
                          additionalElusiveDataContexts
                      ) );
                  }

                  dispatch( updateItem(
                      dataContext,
                      dataObjectItem.id,
                      { statusId: dataObjectItem.attributesModified.get( 'statusId' ) as number },
                      additionalElusiveDataContexts
                  ) );
              }

              // delete
              else {
                  if ( referenceToParentDataObject ) {
                      dispatch( deleteReference(
                          dataContext,
                          referenceToParentDataObject.id,
                          true,
                          additionalElusiveDataContexts,
                          false
                      ) );
                  }

                  dispatch( deleteItem(
                      dataContext,
                      dataObjectItem.id,
                      true,
                      additionalElusiveDataContexts,
                      false,
                      false
                  ) );

                  // deselect if selected
                  if ( selectedLayoutElementId === dataObjectToUse.origId ) {
                      onSelectLayoutElement?.( undefined );
                  }
              }

              // clean up form
              if ( formPanelKey ) {
                  const formPanelKeyWithoutPrefix = formPanelKey.replace( parentUid + '_', '' ),
                        panelProps                = { ...getPanelProps( parentUid ) };

                  if ( panelProps ) {
                      // delete props of form
                      delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix ];
                      delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix + '_attributesForm' ];

                      // delete error of form
                      delete panelProps.panelProps.subPanelErrors[ formPanelKey ];
                      delete panelProps.panelProps.subPanelErrors[ formPanelKey + '_attributesForm' ];

                      setPanelProps( parentUid, panelProps );
                  }

                  // delete elusive data of form
                  dispatch( clearElusiveData( formPanelKey ) );
                  dispatch( clearElusiveData( formPanelKey + '_attributesForm' ) );
              }
          },
          buildAddNodeButton                         = ( nodeId : string, dataObjectToUse : DataStructure, dataStructureToUse : DataStructure ) => {
              const buttonId   = `${uid}_${nodeId}_addButton`,
                    getContent = () => {
                        const dataObjectSchema   = ( dataObjectToUse.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                              dataObjectItemType = metaModel[ dataObjectSchema ].getItemType( dataObjectToUse.item.objectType ?? '' ),
                              parentDataObject   = getParentDataObject( dataObjectToUse.id, dataStructureToUse );

                        if ( dataObjectItemType ) {

                            // TODO: handle circular referenceSets

                            const allowedReferenceSets   = metaModel[ dataObjectSchema ].getReferenceSetsForItemType( dataObjectItemType.id ),
                                  definedReferenceSets   = ( [] as ReferenceSet[] ).concat(
                                          parentDataObject
                                              ? getReferenceSetsForDataObject( dataObjectToUse, dataStructureToUse, dataObjectSchema )
                                              : []
                                      )
                                      .concat(
                                          dataObjectToUse.dataStructure?.length
                                              ? dataObjectToUse.dataStructure
                                                  .reduce(
                                                      ( innerRetVal, childDataStructureToUse ) => {
                                                          innerRetVal = innerRetVal.concat( getReferenceSetsForDataObject( childDataStructureToUse, dataStructureToUse, dataObjectSchema ) );

                                                          return innerRetVal;
                                                      },
                                                      [] as ReferenceSet[]
                                                  )
                                              : []
                                      ),
                                  includeReferenceSetIds = allowedReferenceSets.reduce(
                                      ( innerRetVal, rSet ) => {
                                          if ( !definedReferenceSets.find( dRSet => dRSet.id === rSet.id ) ) {
                                              innerRetVal.push( rSet.id );
                                          }

                                          return innerRetVal;
                                      },
                                      [] as number[]
                                  ),
                                  allowedReferenceSetIds = allowedReferenceSets.reduce(
                                      ( innerRetVal, rSet ) => {
                                          if ( !definedReferenceSets.find( dRSet => dRSet.id === rSet.id ) ) {
                                              innerRetVal[ rSet.id ] = rSet.destItemType.id === dataObjectItemType.id ? 'dest' : 'src';
                                          }

                                          return innerRetVal;
                                      },
                                      {} as { [ referenceSetId : number ] : 'dest' | 'src' }
                                  );

                            return getAddItemDialog( {
                                uid                   : uid + '-AddItemReferenceDialog_button_content',
                                dataContext,
                                schema                : dataObjectSchema,
                                includeReferenceSets  : includeReferenceSetIds,
                                allowedReferenceSetIds: allowedReferenceSetIds,
                                targetItemId          : dataObjectToUse.origId,
                                addMode               : 'new',
                                rawMode               : false,
                                showAdditionalRawNames: false,
                                useThisLabel          : false,
                                onAddNode             : addNewNode.bind( null, dataObjectToUse, dataStructureToUse ),
                                onHide                : () => {
                                    nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                },
                                onInteraction         : () => {
                                    nodeButtons.current[ buttonId ].justInteracted = true;
                                    ignoreSelectionChange.current = true;
                                }
                            } );
                        }
                        else {
                            return <></>;
                        }
                    };

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              return <ContentButton
                  key={buttonId}
                  id={buttonId}

                  ref={el => {
                      if ( el ) {
                          nodeButtons.current[ buttonId ] = {
                              ...nodeButtons.current[ buttonId ],
                              content: el
                          };
                      }
                  }}

                  icon="pi pi-plus-circle yn-add"
                  rounded={true}
                  text={true}
                  horizontalContentAlign="left"
                  verticalContentAlign="bottom"

                  onExpand={() => {
                      const innerRetVal = !!getContent();

                      ignoreSelectionChange.current = true;

                      return innerRetVal;
                  }}
                  onCollapse={( _event, isOutsideClicked ) => {
                      const innerRetVal = !nodeButtons.current[ buttonId ].justInteracted && isOutsideClicked;

                      nodeButtons.current[ buttonId ].justInteracted = false;

                      ignoreSelectionChange.current = true;

                      return innerRetVal;
                  }}
                  content={getContent}
              />;
          },
          buildDeleteNodeButton                      = ( nodeId : string, dataObjectToUse : DataStructure ) => {
              const buttonId              = `${uid}_${nodeId}_deleteButton`,
                    dataObjectItem        = global.data[ dataContext ].items[ dataObjectToUse.origId ],
                    dataObjectItemDeleted = dataObjectItem.isDeleted();

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              return <ContentButton
                  key={buttonId}
                  id={buttonId}

                  ref={el => {
                      if ( el ) {
                          nodeButtons.current[ buttonId ] = {
                              ...nodeButtons.current[ buttonId ],
                              content: el
                          };
                      }
                  }}

                  icon={`pi ${dataObjectItemDeleted ? 'pi-plus-circle yn-dirty' : 'pi-minus-circle yn-delete'}`}
                  rounded={true}
                  text={true}
                  horizontalContentAlign="left"
                  verticalContentAlign="bottom"

                  onExpand={( event ) => {
                      let innerRetVal : boolean = true;

                      if ( event.altKey || dataObjectItemDeleted ) {
                          deleteOrUndeleteNode( dataObjectToUse );

                          innerRetVal = false;
                      }

                      ignoreSelectionChange.current = true;

                      return innerRetVal;
                  }}

                  content={!dataObjectItemDeleted
                      ? <Panel
                          key={`${buttonId}_panel`}
                          id={`${buttonId}_panel`}

                          style={{ width: '25rem' }}
                          className="yn-confirm-option-panel"
                      >
                          <label>
                              <span className="pi pi-question-circle yn-blue"></span>
                              {__( 'YN.base.designer.tree.item.delete.text', { itemKey: dataObjectItem.key, itemName: dataObjectItem.name, itemDisplayAttributeValue: dataObjectItem.displayAttributeValue } )}
                          </label>
                          <Button
                              label={__( 'YN.base.common.no' )}
                              className="p-button-sm p-button-secondary mr-3"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={() => {
                                  ignoreSelectionChange.current = true;

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                          <Button
                              label={__( 'YN.base.common.yes' )}
                              className="p-button-sm p-confirm-popup-accept"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={() => {
                                  deleteOrUndeleteNode( dataObjectToUse );

                                  ignoreSelectionChange.current = true;

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                      </Panel>
                      : null}
              />;
          },
          buildNodeData                              = ( dataObjectToUse : DataStructure, dataStructureToUse : DataStructure, schema? : Schema ) : ExtendTreeNode | undefined => {
              const parentDataObject             = getParentDataObject( dataObjectToUse.id, dataStructureToUse ),
                    dataObjectItem               = global.data[ dataContext ].items[ dataObjectToUse.origId ],
                    dataObjectItemDeleted        = dataObjectItem.isDeleted(),
                    dataObjectKey                = dataObjectToUse.id,
                    dataObjectSchema             = ( schema ?? dataObjectToUse.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                    dataObjectItemType           = metaModel[ dataObjectSchema ].getItemType( dataObjectToUse.item.objectType ?? '' ),
                    dataObjectReferenceSet       = parentDataObject
                        ? getReferenceSetsForDataObject( dataObjectToUse, dataStructureToUse, dataObjectSchema )?.[ 0 ]
                        : undefined,
                    dataObjectReferenceTypeName  = dataObjectToUse.reference?.referenceConfiguration
                        ? Object.keys( dataObjectToUse.reference.referenceConfiguration )[ 0 ] as string
                        : undefined,
                    dataObjectReferenceDirection = dataObjectToUse.reference?.referenceConfiguration
                        ? Object.values( dataObjectToUse.reference.referenceConfiguration )[ 0 ].referenceDirection as string
                        : undefined;

              let retVal : ExtendTreeNode | undefined = undefined;

              if ( dataObjectItemType ) {
                  const allowedReferenceSets = metaModel[ dataObjectSchema ].getReferenceSetsForItemType( dataObjectItemType.id ),
                        definedReferenceSets = ( [] as ReferenceSet[] ).concat(
                                parentDataObject
                                    ? getReferenceSetsForDataObject( dataObjectToUse, dataStructureToUse, dataObjectSchema )
                                    : []
                            )
                            .concat(
                                dataObjectToUse.dataStructure?.length
                                    ? dataObjectToUse.dataStructure
                                        .reduce(
                                            ( innerRetVal, childDataStructureToUse ) => {
                                                innerRetVal = innerRetVal.concat( getReferenceSetsForDataObject( childDataStructureToUse, dataStructureToUse, dataObjectSchema ) );

                                                return innerRetVal;
                                            },
                                            [] as ReferenceSet[]
                                        )
                                    : []
                            );

                  let label = dataObjectItemType.label;

                  if ( dataObjectToUse !== dataStructureToUse ) {
                      label = `${dataObjectToUse.item.name ? ` ${dataObjectToUse.item.name} ( ` : ''}${dataObjectItemType.label}${dataObjectToUse.item.name ? ` )` : ''}`
                  }

                  retVal = {
                      id        : dataObjectKey,
                      key       : dataObjectKey,
                      dataObject: dataObjectToUse,
                      label,
                      selectable: !dataObjectItemDeleted,
                      className : 'yn-dataobject-node',
                      icon      : resolveLigatureIcon( `yn-tree-icon icon-itemtype-detail ${dataObjectItemType.icon}` ),
                      data      : {
                          isDeleted              : dataObjectItemDeleted,
                          buttons                : [],
                          dataDescriptorAlias    : [],
                          layoutElementDefinition: []
                      }
                  };

                  let attributePath : string,
                      dataDescriptor : DataDescriptor | undefined;

                  // special label for referenced Message or Image item
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  if ( ( dataObjectItemType.name === 'Message' || dataObjectItemType.name === 'Image' ) && ( dataObjectReferenceSet || dataObjectReferenceTypeName ) ) {
                      retVal.label = `${_.upperFirst( dataExtraction.extractIsForReferenceTypeName( dataObjectReferenceSet?.referenceTypeLabel ?? dataObjectReferenceTypeName ?? '' ) )} (${dataObjectItemType.label})`;
                  }

                  // dataDescriptorAlias
                  attributePath = 'item.id';
                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );
                  if ( dataDescriptor?.alias ) {
                      retVal.data.dataDescriptorAlias.push( dataDescriptor.alias.substring( 0, dataDescriptor.alias.indexOf( aliasSeparator ) ) );
                  }
                  attributePath = 'reference.id';
                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );
                  if ( dataDescriptor?.alias ) {
                      retVal.data.dataDescriptorAlias.push( dataDescriptor.alias.substring( 0, dataDescriptor.alias.indexOf( aliasSeparator ) ) );
                  }

                  // add button
                  if ( !readOnly && allowedReferenceSets.length - definedReferenceSets.length > 0 && !dataObjectItemDeleted ) {
                      retVal.data.buttons.push( buildAddNodeButton( dataObjectKey, dataObjectToUse, dataStructureToUse ) );
                  }
                  // delete/restore button
                  if ( !readOnly && parentDataObject ) {
                      retVal.data.buttons.push( buildDeleteNodeButton( dataObjectKey, dataObjectToUse ) );
                  }

                  nodeKeyToDataDescriptorAliasesRepresenting.current[ dataObjectKey ] = [];

                  // reference data ( don't show for root )
                  if ( parentDataObject && dataObjectReferenceSet ) {

                      // tooltip
                      if ( dataObjectReferenceDirection && dataObjectReferenceDirection !== '*' ) {
                          if ( dataObjectReferenceDirection === 'SrcToDest' ) {
                              retVal.tooltip = `${__( 'YN.base.common.reference.directionMarkerReversed' )} ${dataObjectReferenceSet.referenceTypeLabel} ${__( 'YN.base.common.reference.directionMarkerReversed' )} ${dataObjectReferenceSet.srcItemType.label}`;
                          }
                          else if ( dataObjectReferenceDirection === 'DestToSrc' ) {
                              retVal.tooltip = `${__( 'YN.base.common.reference.directionMarker' )} ${dataObjectReferenceSet.referenceTypeLabel} ${__( 'YN.base.common.reference.directionMarker' )} ${dataObjectReferenceSet.destItemType.label}`;
                          }
                      }
                      else {
                          retVal.tooltip = dataObjectReferenceSet.label;
                      }
                  }
              }

              return retVal;
          },
          buildTreeData                              = ( dataStructureToUse : DataStructure | undefined ) : ExtendTreeNode[] => {
              const retVal : ExtendTreeNode[]                   = [],
                    newExpandedKeys : TreeTableExpandedKeysType = {};

              if ( dataStructureToUse ) {
                  const schema = ( dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema;

                  const buildNodeDataRecursive = ( dataStructureToProcess : DataStructure ) => {
                      const nodeData = buildNodeData( dataStructureToProcess, dataStructureToUse, schema );

                      if ( nodeData ) {
                          newExpandedKeys[ nodeData.key ] = true;

                          // process children only if not deleted
                          if ( !global.data[ dataContext ].items[ dataStructureToProcess.origId ].isDeleted() && dataStructureToProcess.dataStructure?.length ) {
                              nodeData.children = [];

                              for ( const childDataStructureToProcess of dataStructureToProcess.dataStructure ) {
                                  const childNodeData = buildNodeDataRecursive( childDataStructureToProcess );

                                  if ( childNodeData ) {
                                      newExpandedKeys[ childNodeData.key ] = true;
                                      nodeData.children.push( childNodeData );
                                  }
                              }

                              // sort by label
                              nodeData.children.sort( ( childNodeData1, childNodeData2 ) => {
                                  if ( ( childNodeData1.label ?? '' ) > ( childNodeData2.label ?? '' ) ) {
                                      return 1;
                                  }
                                  else if ( ( childNodeData2.label ?? '' ) > ( childNodeData1.label ?? '' ) ) {
                                      return -1;
                                  }
                                  else {
                                      return 0;
                                  }
                              } )
                          }
                      }

                      return nodeData;
                  };

                  const nodeData = buildNodeDataRecursive( dataStructureToUse );

                  if ( nodeData ) {
                      retVal.push( nodeData );
                  }
              }

              dispatch( setParams( uid, {
                  expandedKeys: newExpandedKeys
              } ) );

              return retVal;
          },
          treeData                                   = useMemo(
              () => {
                  let retVal = elusiveData?.treeData;

                  if (
                      !retVal
                      || dataStructure !== lastParams.current?.dataStructure
                      || dataContext !== lastParams.current?.dataContext
                      || globalDataVersions !== lastParams.current?.globalDataVersions
                      || rootItem !== lastParams.current?.rootItem
                      || language !== lastParams.current?.language
                      || messageCatalogTimestamp !== lastParams.current?.messageCatalogTimestamp
                      || metaModelTimestamp !== lastParams.current?.metaModelTimestamp
                  ) {
                      retVal = buildTreeData( dataStructure );

                      dispatch( setElusiveData( uid, { treeData: retVal } ) );
                  }

                  return retVal;
              },
              [
                  dataContext,
                  dataStructure,
                  globalDataVersions,
                  language,
                  messageCatalogTimestamp,
                  metaModelTimestamp,
                  rootItem
              ]
          ),
          dataDescriptorAliasesInUse                 = useMemo(
              () => {
                  const retVal : { [ dataObjectId : string ] : string[] } = {};

                  if ( dataStructure ) {
                      const dataObjects = extractDataObjectsFromDataStructure( dataStructure );

                      for ( const dataObject of dataObjects ) {
                          const dataObjectItem = global.data[ dataContext ].items[ dataObject.origId ];

                          for ( const referenceId of dataObjectItem?.references ?? [] ) {
                              const reference = global.data[ dataContext ]?.references[ referenceId ];

                              if (
                                  reference
                                  && reference.srcItemId === dataObject.origId
                                  && reference.referenceSet.referenceType.name === 'providesDataFor'
                                  && reference.destItem
                              ) {
                                  let attributePath : string | undefined;

                                  if ( reference.destItem.itemType.name === 'FileField' || reference.destItem.itemType.name === 'ReferenceField' ) {
                                      attributePath = 'reference.id';
                                  }
                                  else {
                                      const destItemProperties = dataExtraction.extractPropertiesOfItem( reference.destItem );

                                      if ( destItemProperties.attributePath ) {
                                          attributePath = destItemProperties.attributePath;
                                      }
                                  }

                                  const dataDescriptor      = attributePath ? buildDataDescriptor( { attributePath }, { item: dataObjectItem } as unknown as DataObjectProperties ) : undefined,
                                        dataDescriptorAlias = dataDescriptor?.alias;

                                  if ( dataDescriptorAlias ) {
                                      if ( !retVal[ dataObject.id ] ) {
                                          retVal[ dataObject.id ] = [];
                                      }

                                      retVal[ dataObject.id ].push( dataDescriptorAlias );
                                  }
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  dataContext,
                  dataStructure,
                  globalDataVersions
              ]
          ),
          markFieldsForAlias                         = ( alias : string | undefined ) : void => {
              setAliasToMark( alias );
          },
          setScrollPosition                          = () => {
              const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

              if ( scrollElement ) {
                  scrollElement.scrollLeft = scrollLeft;
                  scrollElement.scrollTop = scrollTop;
              }
          },
          getScrollPosition                          = () => {
              const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

              if ( scrollElement ) {
                  dispatch( setParams( uid, {
                      scrollTop : scrollElement.scrollTop ?? 0,
                      scrollLeft: scrollElement.scrollLeft ?? 0
                  } ) );
              }
          },
          debouncedGetScrollPosition                 = _.debounce( getScrollPosition.bind( null ), 250 ),
          onContextMenuSelectionChange               = useCallback(
              ( event : TreeSelectionEvent ) => {
                  setContextMenuNode( event.value + '' );
              },
              [
                  'onlyOnce'
              ]
          ),
          onContextMenu                              = useCallback(
              ( event : TreeEventNodeEvent ) => {
                  // disable context menu on static nodes
                  if ( ( event.node as ExtendTreeNode ).dataObject ) {
                      contextMenu.current?.show( event.originalEvent );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          contextMenuModel                           = useMemo(
              () => {
                  const retVal : MenuItem[] = [];

                  if ( contextMenuNode && dataStructure ) {
                      const dataObject = extractDataObjectsFromDataStructure( dataStructure ).find( dS => dS.id === contextMenuNode ) as DataStructure | undefined;

                      if ( dataObject ) {
                          const parentDataObject      = getParentDataObject( dataObject.id, dataStructure ),
                                dataObjectItem        = global.data[ dataContext ].items[ dataObject.origId ],
                                dataObjectItemDeleted = dataObjectItem.isDeleted(),
                                dataObjectSchema      = ( dataObject.item.objectSchema ?? dataStructure.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                                dataObjectItemType    = metaModel[ dataObjectSchema ].getItemType( dataObject.item.objectType ?? '' );

                          if ( dataObjectItemType ) {
                              const allowedReferenceSets = metaModel[ dataObjectSchema ].getReferenceSetsForItemType( dataObjectItemType.id ),
                                    definedReferenceSets = ( [] as ReferenceSet[] ).concat(
                                            parentDataObject
                                                ? getReferenceSetsForDataObject( dataObject, dataStructure, dataObjectSchema )
                                                : []
                                        )
                                        .concat(
                                            dataObject.dataStructure?.length
                                                ? dataObject.dataStructure
                                                    .reduce(
                                                        ( innerRetVal, childDataStructureToUse ) => {
                                                            innerRetVal = innerRetVal.concat( getReferenceSetsForDataObject( childDataStructureToUse, dataStructure, dataObjectSchema ) );

                                                            return innerRetVal;
                                                        },
                                                        [] as ReferenceSet[]
                                                    )
                                                : []
                                        );

                              // add button
                              if ( allowedReferenceSets.length - definedReferenceSets.length > 0 && !dataObjectItemDeleted ) {
                                  const buttonId   = `${uid}_${dataObject.id}_addButton`,
                                        menuItemId = `${dataObject.id}_addItem`;

                                  retVal.push( {
                                      id     : menuItemId,
                                      label  : __( 'YN.base.designer.tree.item.add.title' ),
                                      icon   : 'pi pi-plus-circle yn-green',
                                      data   : {
                                          buttonId
                                      },
                                      command: ( e ) => nodeButtons.current?.[ e.item.data.buttonId! ].content?.expand( new MouseEvent( 'click', { altKey: false, ctrlKey: false, shiftKey: false } ) as unknown as React.MouseEvent )
                                  } );
                              }

                              // delete/restore button
                              if ( parentDataObject ) {
                                  const buttonId = `${uid}_${dataObject.id}_deleteButton`;

                                  if ( retVal.length ) {
                                      retVal.push( { separator: true } );
                                  }

                                  retVal.push( {
                                      id     : `${dataObject.id}_${dataObjectItemDeleted ? 'undeleteItem' : 'deleteItem'}`,
                                      label  : dataObjectItemDeleted ? __( 'YN.base.designer.tree.item.undelete.title' ) : __( 'YN.base.designer.tree.item.delete.title' ),
                                      icon   : `pi ${dataObjectItemDeleted ? 'pi-plus-circle yn-dirty' : 'pi-minus-circle yn-delete'}`,
                                      data   : {
                                          buttonId
                                      },
                                      command: ( e : MenuItemCommandEvent ) => nodeButtons.current?.[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent )
                                  } );
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  contextMenuNode,
                  language,
                  messageCatalogTimestamp,
                  uid
              ]
          ),
          treeNodeTemplate                           = useCallback(
              ( treeNodeData : TreeNode.TreeNode ) : React.ReactNode => {
                  const nodeData        = treeNodeData as ExtendTreeNode,
                        classNames      = ( nodeData.className ?? '' ).split( ' ' ),
                        labelClassNames = [ 'yn-label' ];

                  let retVal : React.ReactNode = null;

                  // handle DataObject nodes
                  if ( dataStructure && nodeData.dataObject ) {
                      const dataObjectItem  = global.data[ dataContext ].items[ nodeData.dataObject.origId ],
                            formPanelErrors = allElusiveData?.[ `${parentUid}_form-${nodeData.dataObject.origId}` ]?.errorData?.count > 0;

                      let tooltip : React.ReactNode = null;

                      // build node tooltip
                      if ( nodeData.tooltip ) {
                          tooltip = <Tooltip target={`#${nodeData.key}`} showDelay={500} position="top">
                              {nodeData.tooltip}
                          </Tooltip>;
                      }

                      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
                      if ( !dataDescriptorAliasesInUse[ nodeData.dataObject?.id ?? 0 ]?.length ) {
                          const index = classNames.findIndex( cM => cM === 'yn-designertree-dataobject-inuse' );

                          if ( index !== -1 ) {
                              classNames.splice( index, 1 );
                          }
                      }
                      else if ( dataDescriptorAliasesInUse[ nodeData.dataObject?.id ?? 0 ]?.length ) {
                          if ( !classNames.includes( 'yn-designertree-dataobject-inuse' ) ) {
                              classNames.push( 'yn-designertree-dataobject-inuse' );
                          }
                      }

                      if ( dataObjectItem.isDeleted() && dataObjectItem.isModified() ) {
                          if ( !classNames.includes( 'yn-deleted' ) ) {
                              classNames.push( 'yn-deleted' );
                          }
                      }
                      else if ( dataObjectItem.isModified() ) {
                          labelClassNames.push( 'yn-dirty' );
                      }
                      else {
                          const index = classNames.findIndex( cM => cM === 'yn-deleted' );

                          if ( index !== -1 ) {
                              classNames.splice( index, 1 );
                          }
                      }

                      if ( formPanelErrors ) {
                          labelClassNames.push( 'yn-error' );
                      }

                      if ( nodeData.dataObject.origId && nodeData.dataObject.origId === selectedLayoutElementId ) {
                          if ( !classNames.includes( 'yn-designertree-node-selected' ) ) {
                              classNames.push( 'yn-designertree-node-selected' );
                          }
                      }
                      else {
                          const index = classNames.findIndex( cM => cM === 'yn-designertree-node-selected' );

                          if ( index !== -1 ) {
                              classNames.splice( index, 1 );
                          }
                      }

                      // build node
                      retVal = <>
                          {tooltip}
                          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                          <div
                              className="flex flex-column flex-auto overflow-hidden yn-designertree-header"
                              draggable="false"
                              onClick={() => {
                                  if ( !ignoreSelectionChange.current ) {
                                      onSelectLayoutElement?.( nodeData.dataObject!.origId );
                                  }

                                  ignoreSelectionChange.current = false;
                              }}
                              onMouseEnter={() => {
                                  if ( nodeData.data.dataDescriptorAlias ) {
                                      onMarkLayoutElementsForAlias?.( nodeData.data.dataDescriptorAlias );
                                  }
                              }}
                              onMouseLeave={() => {
                                  onMarkLayoutElementsForAlias?.( undefined );
                              }}
                          >
                              <div id={`${nodeData.key}`} className="yn-treenode-header">
                                  <span key="label" className={labelClassNames.join( ' ' )}>{nodeData.label ?? ''}</span>
                                  {nodeData.data.buttons}
                              </div>
                          </div>
                      </>;
                  }

                  nodeData.className = classNames.join( ' ' );

                  return retVal;
              },
              [
                  dataContext,
                  dataDescriptorAliasesInUse,
                  dataStructure,
                  language,
                  messageCatalogTimestamp,
                  onMarkLayoutElementsForAlias,
                  onSelectLayoutElement,
                  readOnly,
                  selectedDataDescriptorAlias,
                  selectedLayoutElementId
              ]
          ),
          treeTogglerTemplate                        = useCallback(
              () : React.ReactNode => {
                  return <span className="yn-tree-toggler-placeholder"></span>;
              },
              [
                  'onlyOnce'
              ]
          ),
          content                                    = useMemo(
              () => {
                  let retVal = <></>;

                  if ( dataStructure ) {
                      retVal = <>
                          <ContextMenu
                              key={`${uid}_contextMenu`}
                              model={contextMenuModel}
                              ref={el => contextMenu.current = el}
                              onHide={() => setContextMenuNode( undefined )}
                          />
                          <Tree
                              key={`${uid}_tree`}
                              id={`${uid}_tree`}

                              ref={el => treeElement.current = el?.getElement() ?? null}

                              value={treeData}
                              nodeTemplate={treeNodeTemplate}

                              expandedKeys={expandedKeys}
                              togglerTemplate={treeTogglerTemplate}
                              // dummy on toggle needed to force PrimeReact tree to obey expanded keys property
                              onToggle={() => {
                              }}

                              className="yn-menu-tree yn-node-content-tree yn-designertree"
                              dragdropScope="formdesigner"

                              contextMenuSelectionKey={contextMenuNode}
                              onContextMenuSelectionChange={onContextMenuSelectionChange}
                              onContextMenu={onContextMenu}

                              pt={{
                                  container: {
                                      onScroll: debouncedGetScrollPosition
                                  },
                                  footer   : {
                                      style: {
                                          margin: '1rem -1rem -1rem -1rem',
                                          width : 'calc(100% + 2rem)',
                                          zIndex: '10'
                                      }
                                  }
                              }}
                          />
                      </>;
                  }

                  return retVal;
              },
              [
                  contextMenuModel,
                  contextMenuNode,
                  expandedKeys,
                  onContextMenu,
                  onContextMenuSelectionChange,
                  rootItemId,
                  treeData,
                  treeNodeTemplate,
                  treeTogglerTemplate,
                  uid
              ]
          );

    lastParams.current = {
        dataContext,
        dataStructure,
        rootItem,
        globalDataVersions,
        language,
        messageCatalogTimestamp,
        metaModelTimestamp,
        onSelectLayoutElement,
        rootItemId,
        selectedLayoutElementId,
        treeData,
        uid
    };

    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {
            markFieldsForAlias
        } )
    );

    // set scroll position (on initial render or change)
    useLayoutEffect(
        () => {
            setScrollPosition();
        },
        [
            scrollLeft,
            scrollTop,
            initialRendered.current
        ]
    );

    // mark/unmark aliasToMark
    // TODO: external config
    useLayoutEffect(
        () => {
            // remove current
            let elements = document.querySelectorAll( `.yn-menu-tree#${uid}_tree .yn-designertree-field.yn-designertree-field-using-alias` ),
                index    = 0;

            if ( elements?.length ) {
                while ( elements[ index ] ) {
                    elements[ index++ ].classList.remove( 'yn-designertree-field-using-alias' );
                }
            }

            elements = document.querySelectorAll( `.yn-menu-tree#${uid}_tree .yn-designertree-header-using-alias` );
            index = 0;

            if ( elements?.length ) {
                while ( elements[ index ] ) {
                    elements[ index++ ].classList.remove( 'yn-designertree-header-using-alias' );
                }
            }

            if ( aliasToMark ) {
                elements = document.querySelectorAll( `.yn-menu-tree#${uid}_tree .yn-designertree-field[data-datadescriptoralias="${aliasToMark}"]` );
                index = 0;

                if ( elements?.length ) {
                    while ( elements[ index ] ) {
                        elements[ index++ ].classList.add( 'yn-designertree-field-using-alias' );
                    }
                }
                else {
                    for ( const nodeKey of Object.keys( nodeKeyToDataDescriptorAliasesRepresenting.current ?? {} ) as string[] ) {
                        if ( nodeKeyToDataDescriptorAliasesRepresenting.current[ nodeKey ].includes( aliasToMark ) ) {
                            const element = document.getElementById( nodeKey );

                            //element?.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
                            element?.classList.add( 'yn-designertree-header-using-alias' );
                        }
                    }
                }
            }
        },
        [
            aliasToMark
        ]
    );

    return content;
} );
DesignerTree.defaultProps = defaultProps;
DesignerTree.displayName = 'DesignerTree';


export default DesignerTree;
